import React, { useState, useEffect } from 'react';
import { Container, Table, Form, Button, Row, Col, Spinner, Badge, InputGroup, Alert } from 'react-bootstrap';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { ApiGet, ApiPut } from '../../../../../ApiServices/ApiServices';
import '../../../../../styles/tables.css';

const MenuItemsBulkUpdate = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get branchId and branchName from URL params
  const branchId = searchParams.get('branchId');
  const branchName = searchParams.get('branchName') ? decodeURIComponent(searchParams.get('branchName')) : '';

  const [menuItems, setMenuItems] = useState([]);
  const [originalItems, setOriginalItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);

  // Dietary type options
  const dietaryTypeOptions = [
    { value: 'VEG', label: 'Veg' },
    { value: 'NON_VEG', label: 'Non-Veg' },
    { value: 'VEGAN', label: 'Vegan' },
    { value: 'EGG', label: 'Egg' }
  ];

  // Spice level options
  const spiceLevelOptions = [
    { value: 'MILD', label: 'Mild' },
    { value: 'MEDIUM', label: 'Medium' },
    { value: 'HOT', label: 'Hot' },
    { value: 'EXTRA_HOT', label: 'Extra Hot' }
  ];

  useEffect(() => {
    fetchCategories();
    fetchMenuItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  useEffect(() => {
    fetchMenuItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryFilter]);

  const fetchCategories = async () => {
    try {
      const params = {
        pageNumber: 0,
        pageSize: 1000
      };

      // Filter categories by branchId if provided
      if (branchId) {
        params.branchId = branchId;
      }

      const result = await ApiGet('/api/restaurant/menu_category/filter', params);

      if (result.success) {
        const data = result.success.data.data;
        setCategories(data.records || []);
      }
    } catch (err) {
      toast.error('Failed to fetch categories');
    }
  };

  const fetchMenuItems = async () => {
    setLoading(true);
    try {
      const params = {
        pageNumber: 0,
        pageSize: 1000
      };

      // Add branchId filter if provided
      if (branchId) {
        params.branchId = branchId;
      }

      if (categoryFilter) {
        params.categoryId = categoryFilter;
      }

      const result = await ApiGet('/api/restaurant/menu_items/filter', params);

      if (result.success) {
        const data = result.success.data.data;
        const items = (data.records || []).map(item => ({
          ...item,
          _modified: false
        }));
        // Sort by priority
        items.sort((a, b) => (a.priority || 0) - (b.priority || 0));
        setMenuItems(items);
        setOriginalItems(JSON.parse(JSON.stringify(items)));
        setHasChanges(false);
      } else {
        toast.error(result.fail);
      }
    } catch (err) {
      toast.error('Failed to fetch menu items');
    } finally {
      setLoading(false);
    }
  };

  const handleItemChange = (index, field, value) => {
    setMenuItems(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
        _modified: true
      };
      return updated;
    });
    setHasChanges(true);
  };

  // Drag and drop handlers for priority reordering
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target.outerHTML);
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    setDraggedIndex(null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    setMenuItems(prev => {
      const updated = [...prev];
      const [draggedItem] = updated.splice(draggedIndex, 1);
      updated.splice(dropIndex, 0, draggedItem);

      // Update priorities based on new order
      return updated.map((item, idx) => ({
        ...item,
        priority: idx + 1,
        _modified: true
      }));
    });
    setHasChanges(true);
    setDraggedIndex(null);
  };

  const moveItem = (index, direction) => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= menuItems.length) return;

    setMenuItems(prev => {
      const updated = [...prev];
      [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];

      // Update priorities based on new order
      return updated.map((item, idx) => ({
        ...item,
        priority: idx + 1,
        _modified: true
      }));
    });
    setHasChanges(true);
  };

  const handleSaveAll = async () => {
    const modifiedItems = menuItems.filter(item => item._modified);

    if (modifiedItems.length === 0) {
      toast.info('No changes to save');
      return;
    }

    setSaving(true);
    try {
      const payload = modifiedItems.map(item => ({
        id: item.id,
        name: item.name,
        description: item.description,
        price: parseFloat(item.price) || 0,
        mrp: parseFloat(item.mrp) || 0,
        costPrice: parseFloat(item.costPrice) || 0,
        dietaryType: item.dietaryType,
        isActive: item.isActive,
        isAvailable: item.isAvailable,
        availableOnline: item.availableOnline,
        preparationMinutes: parseInt(item.preparationMinutes) || 0,
        deliveryMinutes: parseInt(item.deliveryMinutes) || 0,
        isRecommended: item.isRecommended,
        spiceLevel: item.spiceLevel,
        priority: parseInt(item.priority) || 0,
        branchId: { id: parseInt(branchId) }
      }));

      const result = await ApiPut(`/api/restaurant/menu_items/updateMultiple`, payload);

      if (result.success) {
        toast.success(`${modifiedItems.length} item(s) updated successfully`);
        // Refresh data to get latest from server
        fetchMenuItems();
      } else {
        toast.error(result.fail || 'Failed to update items');
      }
    } catch (err) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setMenuItems(JSON.parse(JSON.stringify(originalItems)));
    setHasChanges(false);
    toast.info('Changes discarded');
  };

  const handleGoBack = () => {
    navigate('/restaurant/user-management/branches');
  };

  const filteredItems = menuItems.filter(item => {
    if (!searchQuery) return true;
    return item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description?.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <Container fluid className="py-2">
      {/* Header with Branch Info */}
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={handleGoBack}
              title="Back to Branches"
            >
              <i className="bi bi-arrow-left"></i>
            </Button>
            <h2 style={{ color: '#d74440', fontWeight: '700', marginBottom: 0 }}>
              <i className="fas fa-edit me-2"></i>
              Bulk Update Menu Items
            </h2>
          </div>
          {branchId && (
            <Alert variant="info" className="py-2 px-3 mb-0 mt-2 d-inline-block">
              <i className="bi bi-building me-2"></i>
              <strong>Branch:</strong> {branchName || `ID: ${branchId}`}
            </Alert>
          )}
          <div className="mt-2">
            <small className="text-muted">Drag rows to reorder priority. Edit fields inline and save all changes at once.</small>
          </div>
        </div>
        <div className="d-flex gap-2">
          {hasChanges && (
            <Button variant="outline-secondary" onClick={handleReset} disabled={saving}>
              <i className="bi bi-arrow-counterclockwise me-2"></i>
              Reset
            </Button>
          )}
          <Button
            style={{ backgroundColor: '#d74440', borderColor: '#d74440' }}
            onClick={handleSaveAll}
            disabled={saving || !hasChanges}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" style={{ width: '1rem', height: '1rem' }} className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check-all me-2"></i>
                Save All Changes
              </>
            )}
          </Button>
        </div>
      </div>

      {/* No Branch Warning */}
      {!branchId && (
        <Alert variant="warning" className="mb-4">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>No branch selected.</strong> Please select a branch from the
          <Button variant="link" className="p-0 ms-1" onClick={handleGoBack}>
            Branches page
          </Button> to filter menu items.
        </Alert>
      )}

      {/* Filters */}
      <Row className="mb-4 align-items-center">
        <Col md={4}>
          <InputGroup style={{ height: '42px' }}>
            <InputGroup.Text style={{ height: '42px' }}>
              <i className="bi bi-search"></i>
            </InputGroup.Text>
            <Form.Control
              placeholder="Search items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ height: '42px' }}
            />
          </InputGroup>
        </Col>
        <Col md={4}>
          <Select
            options={categories.map((cat) => ({
              value: cat.id,
              label: cat.name
            }))}
            value={categoryFilter ? {
              value: categoryFilter,
              label: categories.find(c => c.id === parseInt(categoryFilter))?.name || ''
            } : null}
            onChange={(selected) => setCategoryFilter(selected ? selected.value : '')}
            isClearable
            isSearchable
            placeholder="Filter by Category"
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={{
              control: (base) => ({
                ...base,
                height: '42px',
                minHeight: '42px'
              }),
              menuPortal: (base) => ({
                ...base,
                zIndex: 9999
              })
            }}
          />
        </Col>
        <Col md={4} className="text-end">
          {hasChanges && (
            <Badge bg="warning" className="py-2 px-3">
              <i className="bi bi-exclamation-triangle me-1"></i>
              Unsaved changes
            </Badge>
          )}
        </Col>
      </Row>

      {/* Table */}
      <div className="table-responsive" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        <Table striped bordered hover className="modern-table" style={{ fontSize: '0.85rem' }}>
          <thead style={{ position: 'sticky', top: 0, zIndex: 10, background: '#fff' }}>
            <tr>
              <th style={{ width: '60px' }}>Order</th>
              <th style={{ width: '50px' }}>ID</th>
              <th style={{ width: '200px' }}>Name</th>
              <th style={{ width: '100px' }}>Price</th>
              <th style={{ width: '100px' }}>MRP</th>
              <th style={{ width: '100px' }}>Dietary</th>
              <th style={{ width: '100px' }}>Spice</th>
              <th style={{ width: '80px' }}>Prep (min)</th>
              <th style={{ width: '60px' }}>Active</th>
              <th style={{ width: '60px' }}>Available</th>
              <th style={{ width: '60px' }}>Online</th>
              <th style={{ width: '60px' }}>Recommended</th>
              <th style={{ width: '60px' }}>Priority</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="13" className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <div className="mt-2">Loading menu items...</div>
                </td>
              </tr>
            ) : filteredItems.length === 0 ? (
              <tr>
                <td colSpan="13" className="text-center py-4 text-muted">
                  <i className="fas fa-inbox me-2"></i>
                  {branchId ? 'No menu items found for this branch' : 'No menu items found'}
                </td>
              </tr>
            ) : (
              filteredItems.map((item, index) => (
                <tr
                  key={item.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                  onDrop={(e) => handleDrop(e, index)}
                  style={{
                    cursor: 'grab',
                    backgroundColor: item._modified ? '#fff3cd' : 'inherit'
                  }}
                >
                  <td>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => moveItem(index, 'up')}
                        disabled={index === 0}
                        style={{ padding: '2px 6px' }}
                      >
                        <i className="bi bi-arrow-up"></i>
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => moveItem(index, 'down')}
                        disabled={index === filteredItems.length - 1}
                        style={{ padding: '2px 6px' }}
                      >
                        <i className="bi bi-arrow-down"></i>
                      </Button>
                    </div>
                  </td>
                  <td><strong>{item.id}</strong></td>
                  <td>
                    <Form.Control
                      type="text"
                      size="sm"
                      value={item.name || ''}
                      onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                      style={{ minWidth: '150px' }}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      size="sm"
                      step="0.01"
                      value={item.price || ''}
                      onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                      style={{ width: '80px' }}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      size="sm"
                      step="0.01"
                      value={item.mrp || ''}
                      onChange={(e) => handleItemChange(index, 'mrp', e.target.value)}
                      style={{ width: '80px' }}
                    />
                  </td>
                  <td>
                    <Form.Select
                      size="sm"
                      value={item.dietaryType || 'VEG'}
                      onChange={(e) => handleItemChange(index, 'dietaryType', e.target.value)}
                      style={{ width: '90px' }}
                    >
                      {dietaryTypeOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Select
                      size="sm"
                      value={item.spiceLevel || 'MEDIUM'}
                      onChange={(e) => handleItemChange(index, 'spiceLevel', e.target.value)}
                      style={{ width: '90px' }}
                    >
                      {spiceLevelOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      size="sm"
                      value={item.preparationMinutes || 0}
                      onChange={(e) => handleItemChange(index, 'preparationMinutes', e.target.value)}
                      style={{ width: '60px' }}
                    />
                  </td>
                  <td className="text-center">
                    <Form.Check
                      type="switch"
                      checked={item.isActive ?? true}
                      onChange={(e) => handleItemChange(index, 'isActive', e.target.checked)}
                    />
                  </td>
                  <td className="text-center">
                    <Form.Check
                      type="switch"
                      checked={item.isAvailable ?? true}
                      onChange={(e) => handleItemChange(index, 'isAvailable', e.target.checked)}
                    />
                  </td>
                  <td className="text-center">
                    <Form.Check
                      type="switch"
                      checked={item.availableOnline ?? true}
                      onChange={(e) => handleItemChange(index, 'availableOnline', e.target.checked)}
                    />
                  </td>
                  <td className="text-center">
                    <Form.Check
                      type="switch"
                      checked={item.isRecommended ?? false}
                      onChange={(e) => handleItemChange(index, 'isRecommended', e.target.checked)}
                    />
                  </td>
                  <td>
                    <Form.Control
                      type="number"
                      size="sm"
                      value={item.priority || 0}
                      onChange={(e) => handleItemChange(index, 'priority', e.target.value)}
                      style={{ width: '50px' }}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Footer Info */}
      <div className="mt-3 d-flex justify-content-between align-items-center">
        <small className="text-muted">
          Total items: {filteredItems.length} | Modified: {menuItems.filter(i => i._modified).length}
        </small>
        {hasChanges && (
          <Button
            style={{ backgroundColor: '#d74440', borderColor: '#d74440' }}
            onClick={handleSaveAll}
            disabled={saving}
          >
            {saving ? (
              <>
                <Spinner animation="border" size="sm" style={{ width: '1rem', height: '1rem' }} className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check-all me-2"></i>
                Save All Changes
              </>
            )}
          </Button>
        )}
      </div>
    </Container>
  );
};

export default MenuItemsBulkUpdate;
